import { beijingMinute, firstPick, pbRow, rangeSpans, retrySkipsFirst, type MemberRow, type OpKey } from "@/app/groupPerformance";
import { PB_STATE, pbDetail, type PrimaryBackupList, type PrimaryBackupRow } from "@/app/health";
import type { ChannelGroup } from "@/app/master";
import { formatRate } from "@/app/traffic";
import { formatTime, KeyValue, Legend, Pill, SectionTitle, seriesColor, ShareBar, Table, Th, type KeyValueItem, type ShareSegment, type Tone } from "@/ui";
import { PB_DEFAULT } from "./channelGroupDraft";

/** 策略面板的输入：成员行已按 priority 排好；costRank 是最低成本策略按成本排好的行下标。 */
export interface PanelProps {
  group: ChannelGroup; rows: MemberRow[]; op: OpKey; now: number;
  /** 成员的显示名：通道名或组名。 */
  name: (r: MemberRow) => string;
  /** 通道 id 到名称，主备状态里的通道用。 */
  channel: (id: number) => string;
  /** 主备状态；pbStale 表示快照过期，worker 读不到状态、一律走主通道。 */
  pb?: PrimaryBackupList; pbStale: boolean; ops: string[];
  costRank?: number[];
  /** 加权策略各成员近 1 分钟的实际占比（0 到 1），没有流量时为 null。 */
  actualShare: (number | null)[];
}

const USABLE: Record<MemberRow["usable"], { tone: Tone; label: string }> = {
  ok: { tone: "success", label: "正常选出" },
  fallback: { tone: "warning", label: "降级选出" },
  off: { tone: "neutral", label: "不可用" },
};

// 选路与熔断按 通道 × 运营商 判定（docs/10 3.2），全部运营商合并后推算不出某一次的选择
const PER_OP = <span className="ax-secondary">按运营商分别判定，选择运营商后显示</span>;

/** 可用性标签；没选运营商时不显示。 */
const usablePill = (r: MemberRow, op: OpKey) => (op === null ? null : <Pill tone={USABLE[r.usable].tone} plain>{USABLE[r.usable].label}</Pill>);

/** 按顺序选第一个可用成员的策略（priority、number_affinity、least_cost）：现在会选谁、是不是降级选出。 */
function pickItem(rows: MemberRow[], order: number[] | undefined, op: OpKey, name: PanelProps["name"]): KeyValueItem {
  if (op === null || !order) return { label: "当前首选", value: PER_OP };
  const i = firstPick(order.map((k) => rows[k]));
  if (i < 0) return { label: "当前首选", value: <Pill tone="danger">没有可用成员，按无路由失败</Pill> };
  const r = rows[order[i]];
  return { label: "当前首选", value: <>{name(r)} {usablePill(r, op)}</> };
}

/** 选路状态面板：按策略的特点说明"现在怎么选、选到谁"（docs/10 第 3 节）；只看通道状态，单条消息另有内容与 sender 的硬过滤。 */
export function GroupStrategyPanel(p: PanelProps) {
  return (
    <section className="ax-summary">
      <SectionTitle>选路状态</SectionTitle>
      <Body {...p} />
    </section>
  );
}

function Body(p: PanelProps) {
  const { group: g, rows, name, op } = p;
  const params = (g.params ?? {}) as Record<string, unknown>;
  const order = rows.map((_, i) => i);
  switch (g.strategy) {
    case "priority":
      return <KeyValue columns={2} items={[pickItem(rows, order, op, name), { label: "规则", value: "按优先级取第一个可用的成员；熔断中的让给其他成员，都被挡时才降级选出" }]} />;
    case "number_affinity":
      return <KeyValue columns={2} items={[pickItem(rows, order, op, name), { label: "号码调整", value: `号码 ${params.recent_days || 7} 天内送达过的通道提到最前，连续失败 ${params.fail_threshold || 2} 次的挪到最后；没有记录的号码按下表顺序` }]} />;
    case "least_cost":
      return <KeyValue columns={2} items={[pickItem(rows, p.costRank, op, name), { label: "规则", value: "按每段成本（换算到基准币种）从低到高，没配成本的排最后，同价按优先级；成本按运营商配置" }]} />;
    case "quality_based":
      return <QualityWeights params={params} />;
    case "weighted":
      return <WeightedShares {...p} />;
    case "time":
      return <TimeLine {...p} />;
    case "primary_backup":
      return <PrimaryBackup {...p} />;
    case "retry":
      return <RetryFlow {...p} params={params} />;
    default:
      return <span className="ax-secondary">这个策略没有专门的状态展示。</span>;
  }
}

/** 按质量：质量、成本、延迟三项的权重按客户档位取，参数里没有的档位用默认权重（D44、D101）。 */
function QualityWeights({ params }: { params: Record<string, unknown> }) {
  const weights = { premium: { quality: 0.7, cost: 0.2, latency: 0.1 }, standard: { quality: 0.5, cost: 0.4, latency: 0.1 }, ...(params.weights as object) } as Record<string, { quality?: number; cost?: number; latency?: number }>;
  const pct = (v: number | undefined, w: { quality?: number; cost?: number; latency?: number }) => `${Math.round(((v ?? 0) / ((w.quality ?? 0) + (w.cost ?? 0) + (w.latency ?? 0))) * 100)}%`;
  return (
    <>
      <span className="ax-secondary">按质量、成本、延迟打分后加权随机，分数高的拿到的流量多；质量用修正送达率。其他档位按 standard。</span>
      <KeyValue columns={2} items={Object.entries(weights).map(([tier, w]) => ({ label: tier, value: `质量 ${pct(w.quality, w)} · 成本 ${pct(w.cost, w)} · 延迟 ${pct(w.latency, w)}`, mono: true }))} />
    </>
  );
}

/** 一组占比画成首尾相接的片段。 */
function stack(shares: (number | null)[], label: (i: number, v: number) => string): ShareSegment[] {
  let at = 0;
  return shares.flatMap((v, i) => {
    if (!v) return [];
    const seg = { key: String(i), start: at, end: at + v, color: seriesColor(i), label: label(i, v) };
    at += v;
    return [seg];
  });
}

/** 按权重：配置的占比与近 1 分钟实际的占比上下对照（决策 D100）。 */
function WeightedShares({ rows, name, actualShare }: PanelProps) {
  const total = rows.reduce((s, r) => s + r.member.weight, 0);
  const config = rows.map((r) => (total ? r.member.weight / total : 0));
  const label = (i: number, v: number) => `${name(rows[i])} ${formatRate(v)}`;
  return (
    <>
      <span className="ax-secondary">在可用成员里按权重抽签；不可用成员的份额按剩下成员的权重分。实际占比取各通道近 1 分钟的全部提交，通道也在别的组合里时会偏高。</span>
      <div className="ax-stack">
        <span className="ax-secondary">配置占比</span>
        <ShareBar label="配置占比" segments={stack(config, label)} />
        <span className="ax-secondary">实际占比 · 近 1 分钟</span>
        {actualShare.some((v) => v !== null) ? <ShareBar label="实际占比" segments={stack(actualShare, label)} /> : <span className="ax-muted">近 1 分钟没有提交</span>}
      </div>
      <Legend items={rows.map((r, i) => ({ name: name(r), color: seriesColor(i) }))} />
    </>
  );
}

/** 按时间：24 小时（北京时间）的时段轴，标出现在与正在生效的成员；生效成员不可用就无路由。 */
function TimeLine({ rows, name, now, op }: PanelProps) {
  const minute = beijingMinute(now);
  const segments = rows.flatMap((r, i) => (r.member.time_start && r.member.time_end ? rangeSpans(r.member.time_start, r.member.time_end) : []).map(([s, e], k) => ({
    key: `${i}-${k}`, start: s, end: e, color: seriesColor(i), current: r.inSlot, label: `${r.member.time_start}–${r.member.time_end} ${name(r)}`,
  })));
  const clock = `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
  const cur = rows.find((r) => r.inSlot);
  const warn = cur && op !== null && cur.usable !== "ok" && <Pill tone={cur.usable === "off" ? "danger" : "warning"} plain>{cur.usable === "off" ? "不可用，按无路由失败" : "熔断中，降级选出"}</Pill>;
  return (
    <>
      <KeyValue columns={2} items={[
        { label: "当前时段", value: cur ? <>{name(cur)} <span className="ax-mono ax-secondary">{cur.member.time_start}–{cur.member.time_end}</span> {warn}</> : "—" },
        { label: "北京时间", value: clock, mono: true },
      ]} />
      <ShareBar label="24 小时时段，北京时间" segments={segments} marker={{ at: minute / 1440, label: `现在 ${clock}` }} scale={["00:00", "06:00", "12:00", "18:00", "24:00"]} />
      <Legend items={rows.map((r, i) => ({ name: name(r), color: seriesColor(i) }))} />
    </>
  );
}

/** 主备：选了运营商时看这个运营商上的状态与流量分配；没选时列出各运营商的状态（docs/10 第 9 节，决策 D92）。快照过期时一律按主用显示。 */
function PrimaryBackup({ group, rows, name, channel, pb, pbStale, op, ops }: PanelProps) {
  const params = { ...PB_DEFAULT, ...(group.params as object) };
  const stateOf = (o: string): PrimaryBackupRow | undefined => (pbStale ? undefined : pbRow(pb, group.id, o));
  const primary = rows[0] ? name(rows[0]) : "—";
  const pill = (r?: PrimaryBackupRow) => (r ? <Pill tone={PB_STATE[r.state].tone}>{PB_STATE[r.state].label}</Pill> : <Pill tone="success">主用</Pill>);
  const rule = `主通道拿其余流量，备用合占 ${params.probe_ratio / 10}%；备用近 3 分钟送达率高出 ${params.threshold} 个百分点且连续 2 分钟成立就切过去，切回阈值 ${params.recover_threshold} 个百分点`;
  const stale = pbStale && <span className="ax-secondary">指标快照已过期：worker 读不到主备状态，所有运营商都按主用走主通道。</span>;
  if (op !== null) {
    const r = stateOf(op);
    const active = rows.find((x) => x.member.channel_id === (r?.active_channel_id ?? rows[0]?.member.channel_id));
    return (
      <>
        {stale}
        <KeyValue columns={2} items={[
          { label: "状态", value: pill(r) },
          // 当前通道被过滤掉时这条消息按 priority 取剩下的第一个候选，不分流（docs/10 第 9 节"选路"）
          { label: "当前通道", value: <>{r ? channel(r.active_channel_id) : primary} {active && active.usable !== "ok" && <Pill tone="warning" plain>{active.usable === "off" ? "不可用" : "熔断中"}，按优先级取剩下的第一个</Pill>}</> },
          { label: "说明", value: r ? pbDetail(r, channel) || "—" : rule },
          { label: "状态起始", value: r ? formatTime(r.since) : "—", mono: true },
        ]} />
      </>
    );
  }
  return (
    <>
      <span className="ax-secondary">{rule}。各运营商分别判定，选择运营商可看成员的流量角色。</span>
      {stale}
      {ops.length ? (
        <Table dense>
          <thead><tr><Th w="op">运营商</Th><Th w="status">状态</Th><Th w="name">当前通道</Th><Th>说明</Th></tr></thead>
          <tbody>
            {ops.map((o) => {
              const r = stateOf(o);
              return <tr key={o}><td className="ax-mono">{o}</td><td>{pill(r)}</td><td>{r ? channel(r.active_channel_id) : primary}</td><td className="ax-secondary">{r ? pbDetail(r, channel) : ""}</td></tr>;
            })}
          </tbody>
        </Table>
      ) : <span className="ax-muted">成员还没有流量。</span>}
    </>
  );
}

/** 失败重试：首发成员 → 重试成员，超时与次数；选了运营商时推算首发是否改发给重试成员（docs/10 4.2）。 */
function RetryFlow({ rows, name, op, params }: PanelProps & { params: Record<string, unknown> }) {
  const [first, retry] = rows;
  if (!first || !retry) return <span className="ax-secondary">失败重试要恰好两个成员。</span>;
  const skip = op !== null && retrySkipsFirst(first.usable, retry.usable);
  return (
    <KeyValue columns={2} items={[
      { label: "首发", value: <>{name(first)} {skip ? <Pill tone="warning" plain>跳过，首发直接发给重试成员</Pill> : usablePill(first, op)}</> },
      { label: "重试", value: <>{name(retry)} {usablePill(retry, op)}</> },
      { label: "触发", value: `尝试开始后 ${params.timeout_sec} 秒内没有送达回执，或已确定失败` },
      { label: "次数", value: `最多 ${params.max_attempts} 次（含首发），已试过的通道不再选` },
    ]} />
  );
}
